import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { route, when } from '../../../../../services/utils';
import { map } from 'rxjs/operators';
import {
  CROP_FIELD_VIEW,
  CROP_FIELDS_AND_WORKS_INDEX,
} from '../../../../../router/routes';
import Field from '../../../../../interfaces/field';
import { useService } from '../../../../../services/services';
import Form, { SubmitType } from '../../../../../components/form';
import Button from '../../../../../components/form/elements/button';
import Group from '../../../../../components/form/elements/group';
import Link from '../../../../../components/form/elements/link';
import Select from '../../../../../components/form/elements/select';
import Text from '../../../../../components/form/elements/text';
import TextArea from '../../../../../components/form/elements/text-area';
import * as yup from 'yup';
import css from './view.module.css';
import ClassifierGroup from '../../../../../enums/classifier-group';
import Classifier from '../../../../../enums/classifier';
import Watch from '../../../../../components/form/elements/watch';
import ClassifierValue from '../../../../../interfaces/classifierValue';
import Paginate from '../../../../../interfaces/paginate';
import ClassifierPropertyKey from '../../../../../enums/classifier-property-key';
import Hidden from '../../../../../components/form/elements/hidden';
import TabsKeys from '../../../../../enums/tab-keys';
import { Section } from '../../../../../pages/crop/fields-and-works';
import indexCss from '../../index.module.css';
import Waypoint from '../../../../../components/form/elements/waypoint';
import Map from '../../../../../components/form/elements/map';
import Readonly from '../../../../../components/form/elements/readonly';

export default function CreateField() {
  const { t } = useTranslation();
  const history = useHistory();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const { fieldService, classifierService, cropChangePlanService } =
    useService();
  const [field, setField] = useState<{
    lad_fields?: Field | Field[];
    other_fields?: Field | Field[];
    season_id: string;
    crop_group_id: { value: string; label: string };
    crop_id: { value: string; label: string };
  }>({
    season_id: season,
    crop_group_id: { value: '', label: '' },
    crop_id: { value: '', label: '' },
  });
  const searchParams = new URLSearchParams(history.location.search);
  const isLad = !!searchParams.get('lad');

  useEffect(() => {
    const subscription = fieldService
      .index(farmId, season, {}, true)
      .subscribe((res: Paginate<Field>) => {
        setField((old) => ({
          ...old,
          map_loaded: true,
          other_fields: res.data as Field[],
        }));
      });

    return () => subscription.unsubscribe();
  }, [farmId, season, fieldService, isLad]);

  const schema = yup.object().shape({
    season_id: yup
      .number()
      .min(new Date().getFullYear() - 5)
      .max(new Date().getFullYear() + 4)
      .required(),
    lad_number: yup.string().max(255).required(),
    name: yup.string().max(255).required(),
    geometry: yup.string().required(),
    block_number: yup.string().max(255).nullable(),
    crop_group_id: when(
      (value) => !!value?.value,
      yup.object().shape({
        value: yup.string().uuid(),
      }),
      yup.object().shape({
        value: yup.string().nullable(),
      })
    ),
    crop_id: when(
      (value) => !!value?.value,
      yup.object().shape({
        value: yup.string().uuid(),
      }),
      yup.object().shape({
        value: yup.string().nullable(),
      })
    ),
    breed: yup.string().max(255).nullable(),
    ownership_id: when(
      (value) => !!value?.value,
      yup.object().shape({
        value: yup.string().uuid(),
      }),
      yup.object().shape({
        value: yup.string().nullable(),
      })
    ),
    notes: yup.string().nullable(),
  });

  const transKeyPrefix = 'pages.crop.fields_and_works.fields.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const submit = useCallback<SubmitType<Field>>(
    (form: any) => {
      form.crop_group_id = form.crop_group_id?.value;
      form.crop_id = form.crop_id?.value;
      form.ownership_id = form.ownership_id?.value;
      return [
        fieldService.store(farmId, form),
        (response: Field) =>
          history.push(
            route(CROP_FIELD_VIEW, {
              farmId: farmId,
              season: season,
              id: response.id,
              tab: TabsKeys.basic_info,
            })
          ),
      ];
    },
    [fieldService, history, farmId, season]
  );

  const [body] = useState(() => [
    Group([
      Hidden('season_id'),
      Text('lad_number', transKeyPrefix + 'lad_number'),
      Text('name', transKeyPrefix + 'name'),
      Readonly([Text('area', transKeyPrefix + 'area')]),
      Text('block_number', transKeyPrefix + 'block_number'),
      Watch(
        Select('crop_group_id', {
          asyncOptions: (value, loadedOptions) => {
            return cropChangePlanService
              .productCategoriesWithTypes(farmId)
              .pipe(
                map((response: any) => ({
                  options: Object.values(response)
                    .map((v: any) => ({
                      label: v.cropGroup.label,
                      value: v.cropGroup.id,
                    }))
                    .filter((value: any) => value.label !== 'Neattiecas'),
                }))
              );
          },
          multi: false,
          clearable: true,
          label: transKeyPrefix + 'crop_group',
        }),
        ({ value, setValue, getValues }) => {
          const crop = getValues('crop_id');
          const breed = getValues('breed');

          if (value.value) {
            if (value.value !== breed?.crop_group?.id) {
              setValue('breed', null);
            }

            if (value.value !== crop?.crop_group?.id) {
              setValue('crop_id', null);
            }
          }
        }
      ),
      Watch(
        Select('crop_id', {
          asyncOptions: (value, loadedOptions, { page, getValues }) => {
            const params: any = { page, value };

            const cropGroupId = getValues('crop_group_id')?.value;

            if (cropGroupId) {
              params.crop_group = cropGroupId;
            }

            return classifierService
              .valuesPaged(
                ClassifierGroup.WAREHOUSE,
                Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                params
              )
              .pipe(
                map((response: Paginate<ClassifierValue>) => {
                  return {
                    hasMore: response.last_page > response.current_page,
                    options: response.data.map((v) => ({
                      label: v.value,
                      value: v.id,
                      crop_group: Object.values(v.properties)
                        .filter(
                          (p) =>
                            p.property.key === ClassifierPropertyKey.CROP_GROUP
                        )
                        .map((p) => {
                          const pv = p.value.pop();
                          return { id: pv.id, label: pv.label };
                        })
                        .pop(),
                    })),
                    additional: { page: response.current_page + 1 },
                  };
                })
              );
          },
          cacheOn: ['crop_group_id'],
          multi: false,
          clearable: true,
          label: transKeyPrefix + 'crop',
        }),
        ({ value, setValue, getValues }) => {
          const cropGroup = getValues('crop_group_id');
          const breed = getValues('breed');

          if (value.value) {
            if (value.crop_group?.id !== cropGroup) {
              setValue('crop_group_id', {
                value: value.crop_group?.id ?? '',
                label: value.crop_group?.label ?? '',
              });
            }

            if (value.value !== breed?.crop?.id) {
              setValue('breed', null);
            }
          }
        }
      ),
      Text('breed', transKeyPrefix + 'breed'),
      Select('ownership_id', {
        asyncOptions: (value, loadedOptions, { page, getValues }) =>
          classifierService
            .valuesPaged(ClassifierGroup.CROP, Classifier.OWNERSHIP, {
              page,
              value,
            })
            .pipe(
              map((response: Paginate<ClassifierValue>) => {
                return {
                  hasMore: response.total === response.per_page,
                  options: response.data
                    .map((v) => ({
                      label: v.value,
                      value: v.id,
                    }))
                    .filter((value) => value.label !== 'Neattiecas'),
                  additional: { page: response.current_page + 1 },
                };
              })
            ),
        multi: false,
        label: transKeyPrefix + 'ownership',
      }),
      Waypoint('waypoints', transKeyPrefix + 'waypoints'),
      Map(isLad ? 'identify' : 'create', 'other_fields'),
      TextArea('notes', transKeyPrefix + 'notes'),
    ]),
  ]);

  const [footer] = useState(() => [
    Button('create'),
    Link(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.fields,
      }),
      'cancel'
    ),
  ]);

  return (
    <div className={`${indexCss['container']} ${indexCss['show-map']}`}>
      <Form
        title={tp('create')}
        mode="create"
        body={body}
        footer={footer}
        values={field}
        submit={submit}
        schema={schema}
        className={css['field-form']}
      />
    </div>
  );
}
